import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import PublicNavbar from './PublicNavbar';
import { useAuth } from '../contexts/AuthContext';
import AppIcon, { AppIconName } from './AppIcon';
import './Help.css';

type HelpSection = {
  id: string;
  icon: AppIconName;
  title: string;
  summary: string;
  keywords: string[];
  content: React.ReactNode;
};

const Help: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');

  const sections: HelpSection[] = useMemo(() => [
    {
      id: 'getting-started',
      icon: 'rocket',
      title: 'Getting started',
      summary: 'Create an account, add your first investments, and build a complete net-worth view.',
      keywords: ['register', 'login', 'account', 'start', 'setup', 'first holding'],
      content: (
        <>
          <h3>Recommended setup order</h3>
          <ol className="help-steps">
            <li><strong>Create an account or sign in.</strong> Registration asks for your name, username, email, and password. You remain signed in on the current browser until you log out or the session becomes invalid.</li>
            <li><strong>Add stocks and mutual funds.</strong> Use Portfolio for manual transactions or imports. You can also browse the Stocks and Mutual Funds catalogues and add an instrument from its detail page.</li>
            <li><strong>Add other assets.</strong> Open Assets to record NPS, PPF, EPF, gold, insurance, property, fixed deposits, and bank balances.</li>
            <li><strong>Review the Dashboard.</strong> Once data is available, the dashboard summarizes value, gains, allocation, market indices, and items that may need attention.</li>
            <li><strong>Open Analysis.</strong> Review net worth distribution, liquidity, concentration, growth assumptions, and long-term projections.</li>
          </ol>
          <Alert variant="info" className="mb-0">
            Start with official transaction records where possible. Correct dates, quantities, and prices are required for cost, gain, and XIRR calculations.
          </Alert>
        </>
      )
    },
    {
      id: 'dashboard',
      icon: 'dashboard',
      title: 'Dashboard',
      summary: 'Understand your portfolio snapshot, market panels, allocation, and daily signals.',
      keywords: ['dashboard', 'indices', 'gainers', 'losers', 'allocation', 'snapshot', 'market'],
      content: (
        <>
          <p>The Dashboard is the daily overview of your StockHunt data. Its totals combine your investment portfolio with supported assets where applicable.</p>
          <div className="help-detail-grid">
            <div><strong>Portfolio snapshot</strong><span>Current value, invested amount, gain or loss, and the split between mutual funds and stocks.</span></div>
            <div><strong>Market indices</strong><span>Current index readings supplied by the market-data service. Values may be delayed.</span></div>
            <div><strong>Top movers</strong><span>Market gainers and losers available from the current data feed.</span></div>
            <div><strong>Value breakdown</strong><span>A comparison of portfolio holdings, retirement assets, property, deposits, and liquid balances.</span></div>
            <div><strong>What matters now</strong><span>Contextual observations based on missing data, allocation, gains, and the completeness of your workspace.</span></div>
            <div><strong>Quick actions</strong><span>Shortcuts to Portfolio, Stocks, Mutual Funds, Assets, and Wealth Analysis.</span></div>
          </div>
          <p className="help-note">If a card shows zero or an empty state, add the related holding or asset first. Refresh the page after a recent import if the totals have not updated.</p>
        </>
      )
    },
    {
      id: 'portfolio',
      icon: 'portfolio',
      title: 'Portfolio and transactions',
      summary: 'Track stock and mutual-fund holdings, costs, returns, notes, and transaction history.',
      keywords: ['portfolio', 'holding', 'transaction', 'buy', 'sell', 'sip', 'redeem', 'cost', 'gain', 'xirr'],
      content: (
        <>
          <h3>Portfolio summary</h3>
          <p>The summary cards show total current value, total cost, and total gain or loss. Holdings without a usable live market price may be excluded from current-value totals and marked with a warning.</p>

          <h3>Mutual Funds and Stocks tabs</h3>
          <p>Each table shows the instrument, current value, today's movement, gain or loss, and XIRR when enough dated cash flows exist. XIRR is visible directly in the table and on mobile holding cards. Open View details for units or shares, cost, current NAV or price, the XIRR explanation, notes and transaction history.</p>

          <h3>Add a transaction</h3>
          <ol className="help-steps">
            <li>Select <strong>Add Transaction</strong> on the Portfolio page.</li>
            <li>Choose Stock or Mutual Fund and select an existing holding.</li>
            <li>Enter the transaction date and type: Buy/Sell for stocks; Purchase/SIP/Redeem for mutual funds.</li>
            <li>Enter quantity or units and the per-unit price or NAV. Review the calculated total before saving.</li>
          </ol>

          <h3>Add a new holding</h3>
          <p>Use <strong>Add to Portfolio</strong> for stocks or <strong>Add Mutual Fund</strong> for funds. Search the catalogue, select the correct instrument, and enter the optional opening transaction. Mutual funds also support an optional folio number.</p>

          <Alert variant="warning" className="mb-0">
            Do not directly edit displayed shares or units to correct a balance. Add the missing buy, sell, redemption, or adjustment through an import so the transaction history and return calculations remain consistent.
          </Alert>
        </>
      )
    },
    {
      id: 'imports',
      icon: 'import',
      title: 'Importing statements',
      summary: 'Supported broker files, CAS documents, passwords, reconciliation behavior, and safe import practices.',
      keywords: ['import', 'csv', 'xlsx', 'pdf', 'cas', 'cams', 'kfintech', 'zerodha', 'groww', 'upstox', 'angel', 'icici', 'cdsl', 'nsdl', 'legacy', 'unassigned', 'move', 'accounts'],
      content: (
        <>
          <p>Open <strong>Investments → Import</strong>. Broker accounts here are saved labels for statement ownership, separate from the bank accounts recorded in Assets. Add each broker label once and reuse it for future uploads; this does not connect to your broker or require login credentials.</p>
          <h3>First time: move Legacy / Unassigned holdings</h3>
          <p>Older stock holdings were kept under Legacy / Unassigned because their broker was not recorded. StockHunt cannot safely guess which broker owns them.</p>
          <ol>
            <li>If the correct broker account is already listed, reuse it. Otherwise click <strong>Add account</strong>, choose Zerodha, Upstox or your broker, enter a recognizable account name and save. Last digits are optional.</li>
            <li>Click <strong>Review and move legacy holdings</strong> or <strong>Move holdings</strong> on the legacy account.</li>
            <li>Choose the destination account and select only the stocks it owns. If all holdings belong to one broker, keep Select all checked. For multiple brokers, move each group separately.</li>
            <li>Review and click <strong>Confirm move</strong>. Holding quantities, costs, transactions and adjustments are preserved, including closed positions.</li>
          </ol>
          <Alert variant="warning">Move your existing legacy history before uploading more broker trades. Do not re-upload that history into a new account to move it: that can duplicate holdings across accounts. If a selected stock already exists in the destination, or has account-specific statement or corporate-action records, the move is blocked for review.</Alert>
          <h3>Upload a Zerodha or Upstox tradebook</h3>
          <ol>
            <li><strong>Provider and account:</strong> select Zerodha Tradebook or Upstox Tradebook, then choose its saved broker account and click Continue.</li>
            <li><strong>Choose statement:</strong> select the original CSV or XLSX exported by the broker, up to 20 MB, then click Review statement. Other supported formats are listed below.</li>
            <li><strong>Review and import:</strong> check the file name, destination account and preview, then confirm import. Records are added only after confirmation.</li>
          </ol>
          <p>The preview shows source rows for CSV/XLSX; final transaction counts and duplicate checks are applied during import. Keeping the correct broker selected separates trades, snapshots and corporate-action checks.</p>
          <div className="help-table-wrap">
            <table className="help-table">
              <thead><tr><th>Import type</th><th>Accepted file</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr><td>Mutual Fund Transactions</td><td>CSV</td><td>Imports dated fund purchases, SIPs, and redemptions.</td></tr>
                <tr><td>Zerodha Tradebook</td><td>CSV or XLSX</td><td>Imports stock trades from a Zerodha tradebook export.</td></tr>
                <tr><td>Zerodha Holdings Reconciliation</td><td>CSV or XLSX</td><td>Aligns quantity and cost using adjustment entries; it does not invent buy/sell trades.</td></tr>
                <tr><td>Groww, Upstox, Angel One, ICICI Direct</td><td>CSV or XLSX</td><td>Imports broker tradebook or order-history exports.</td></tr>
                <tr><td>Other Broker Tradebook</td><td>CSV or XLSX</td><td>Requires recognizable symbol/ISIN, date, side, quantity, and price columns.</td></tr>
                <tr><td>CAMS or CAMS + KFintech CAS</td><td>PDF</td><td>Imports mutual-fund transactions from consolidated statements.</td></tr>
                <tr><td>CDSL or NSDL eCAS</td><td>PDF</td><td>Imports supported holdings and transactions from depository statements.</td></tr>
              </tbody>
            </table>
          </div>
          <h3>PDF passwords</h3>
          <p>CAMS/KFintech statements commonly use the PAN in uppercase. CDSL and NSDL statements use the password described in the statement email. The password is sent only for processing the uploaded document.</p>
          <h3>Before and after importing</h3>
          <ul>
            <li>Use the original downloaded file; avoid edited spreadsheets or scanned PDFs.</li>
            <li>Check the selected import type, saved account, and file extension before upload.</li>
            <li>After completion, compare quantities and costs against the official broker or registrar statement.</li>
            <li>Inspect the transaction list for a holding if totals appear unexpected.</li>
          </ul>
          <Alert variant="info" className="mb-0">
            Splits, bonus issues, and ISIN changes found in an import are saved as evidence for admin review, not guessed as buys or sells. After an approved action, StockHunt compares its result with the latest final statement for that exact account. If they disagree, the statement quantity wins and the item is sent for manual review.
          </Alert>
        </>
      )
    },
    {
      id: 'market-research',
      icon: 'search',
      title: 'Stocks and mutual-fund research',
      summary: 'Search instruments, read detail pages, inspect charts and financials, and add holdings.',
      keywords: ['stocks', 'mutual funds', 'search', 'research', 'nav', 'price', 'financials', 'news', 'isin', 'amfi'],
      content: (
        <>
          <h3>Stocks</h3>
          <p>Search by symbol, company name, or ISIN. A stock detail page may include current price, P/E, PEG, EPS, market capitalization, earnings growth, 52-week range, company information, recent news, price history, income statement, balance sheet, and cash flow.</p>
          <h3>Mutual funds</h3>
          <p>Search by fund name or ISIN. A fund detail page shows its AMFI code, ISIN, current NAV, category and fund house when available, selectable NAV history ranges, recent NAV records, and a description.</p>
          <h3>Add to Portfolio</h3>
          <p>The detail-page button creates the holding. Record the actual purchase or opening transaction with the correct date, units, and price so portfolio calculations represent your investment history.</p>
          <p className="help-note">Catalogue and market information comes from external public data services. Missing metrics do not necessarily indicate a problem with the investment itself.</p>
        </>
      )
    },
    {
      id: 'assets',
      icon: 'assets',
      title: 'Assets and net worth',
      summary: 'Record retirement accounts, gold, insurance, property, deposits, and bank balances.',
      keywords: ['assets', 'nps', 'ppf', 'epf', 'gold', 'lic', 'insurance', 'property', 'real estate', 'fixed deposit', 'bank'],
      content: (
        <>
          <p>The Assets page extends StockHunt beyond market investments. Select an asset card to see account-level detail, then use its action button to add, import, edit, or update data.</p>
          <div className="help-table-wrap">
            <table className="help-table">
              <thead><tr><th>Asset</th><th>How to add it</th><th>Key information used</th></tr></thead>
              <tbody>
                <tr><td>NPS</td><td>Import a KFintech/CAMS statement in PDF, CSV, or JSON.</td><td>Account label, family member, statement values, and optional PRAN/PDF password.</td></tr>
                <tr><td>PPF</td><td>Add accounts manually.</td><td>Balance, rate, monthly contribution, provider, and valuation date.</td></tr>
                <tr><td>EPF</td><td>Import an official EPFO passbook PDF or enter a member manually.</td><td>Employee, employer, pension shares, rate, contribution, UAN/member reference, and date.</td></tr>
                <tr><td>Gold</td><td>Add or update manually.</td><td>Digital/physical type, grams, buy price, current price per gram, and date.</td></tr>
                <tr><td>LIC / Insurance</td><td>Add each policy manually.</td><td>Premiums, sum assured, current value, accrued bonus, due date, and maturity.</td></tr>
                <tr><td>Real Estate</td><td>Add each property manually.</td><td>Cost, current value, outstanding loan, location, and monthly rent.</td></tr>
                <tr><td>Fixed Deposits</td><td>Add each deposit manually.</td><td>Principal, interest rate, start and maturity dates, bank, and optional current value.</td></tr>
                <tr><td>Bank Accounts</td><td>Add each account manually.</td><td>Account type, current balance, bank, masked reference, and last-updated date.</td></tr>
              </tbody>
            </table>
          </div>
          <Alert variant="info" className="mb-0">
            Use labels and masked references that help you distinguish family accounts without storing complete account numbers in notes.
          </Alert>
        </>
      )
    },
    {
      id: 'analysis',
      icon: 'analysis',
      title: 'Financial analysis',
      summary: 'Interpret allocation, monthly growth, liquidity, concentration, assumptions, and future-value estimates.',
      keywords: ['analysis', 'net worth', 'projection', 'cagr', 'growth', 'income', 'monthly', 'liquidity', 'concentration', 'allocation', 'future'],
      content: (
        <>
          <p>Analysis combines the portfolio and recorded assets into a single wealth view. It is a planning aid based on the data you entered and the assumptions shown on the page.</p>
          <div className="help-detail-grid">
            <div><strong>Total net worth</strong><span>Current portfolio value plus the current values of supported assets.</span></div>
            <div><strong>Invested capital</strong><span>Recorded cost or contributions across holdings and assets.</span></div>
            <div><strong>Unrealized gain</strong><span>Current value less recorded invested capital.</span></div>
            <div><strong>Liquid allocation</strong><span>The share represented by bank balances and fixed deposits.</span></div>
            <div><strong>Distribution</strong><span>Current weights by investment or asset bucket.</span></div>
            <div><strong>Capital by purpose</strong><span>Groups wealth into categories such as growth, retirement, real assets, income, and liquid reserves.</span></div>
            <div><strong>Projected monthly growth</strong><span>The monthly equivalent of each asset's displayed annual return, based on its current value.</span></div>
          </div>
          <h3>Projections</h3>
          <p>Future estimates use an asset’s available XIRR or interest rate. When neither exists, StockHunt uses a conservative default assumption. Known monthly contributions are included where the asset record provides them.</p>
          <Alert variant="warning" className="mb-0">
            Projected monthly growth includes appreciation and interest; it is not guaranteed cash income. Projections are scenarios, not forecasts or guarantees. Update stale balances and rates before using them for planning.
          </Alert>
        </>
      )
    },
    {
      id: 'calculations',
      icon: 'calculator',
      title: 'How calculations work',
      summary: 'Definitions for cost, current value, gain/loss, allocation, XIRR, and projections.',
      keywords: ['calculation', 'xirr', 'gain', 'loss', 'cost basis', 'current value', 'return', 'formula'],
      content: (
        <>
          <dl className="help-definitions">
            <div><dt>Current value</dt><dd>Units or shares multiplied by the latest available NAV or market price. For manually maintained assets, it is the latest recorded or calculated value.</dd></div>
            <div><dt>Cost / investment</dt><dd>The recorded acquisition cost or contributed amount after supported purchases, sales, redemptions, and adjustments.</dd></div>
            <div><dt>Gain or loss</dt><dd>Current value minus cost. The percentage return divides that difference by cost when cost is greater than zero.</dd></div>
            <div><dt>XIRR</dt><dd>An annualized return calculated from irregular dated cash flows. It needs valid transaction dates and both invested and ending values.</dd></div>
            <div><dt>Allocation</dt><dd>An item’s current value divided by the combined current value included in that view.</dd></div>
            <div><dt>Projected value</dt><dd>Current value compounded using the displayed growth assumption, plus known recurring contributions where available.</dd></div>
          </dl>
          <p className="help-note">Rounding can cause small differences from a broker statement. Larger differences usually come from missing trades, corporate actions, fees, an incorrect symbol, or a stale/missing market price.</p>
        </>
      )
    },
    {
      id: 'data-management',
      icon: 'data',
      title: 'Editing and deleting data',
      summary: 'Correct notes, inspect history, clear transactions, and understand destructive actions.',
      keywords: ['edit', 'delete', 'clear', 'manage data', 'transactions', 'adjustments', 'notes'],
      content: (
        <>
          <h3>Edit a holding</h3>
          <p>The Portfolio edit action updates notes. Quantity is transaction-derived and cannot be safely corrected by changing the displayed total.</p>
          <h3>Review transactions</h3>
          <p>Use the transaction-list action beside a holding to verify date, type, quantity, price, amount, and notes.</p>
          <h3>Clear imported or entered activity</h3>
          <p>Portfolio → Import → Manage Data can clear all mutual-fund transactions, all stock transactions, or the history for one selected holding. Clearing stock data also removes stock adjustment entries. Holding notes are preserved, but quantities and costs reset to zero.</p>
          <h3>Delete asset records</h3>
          <p>Asset detail panels provide edit and delete actions for individual accounts, members, policies, properties, deposits, or bank accounts. Confirm the prompt only after checking that you selected the correct record.</p>
          <Alert variant="danger" className="mb-0">
            Clear and delete actions are destructive. Keep the original statement available so the data can be imported or entered again if needed.
          </Alert>
        </>
      )
    },
    {
      id: 'troubleshooting',
      icon: 'tools',
      title: 'Troubleshooting',
      summary: 'Resolve missing prices, failed imports, incorrect totals, empty charts, and sign-in issues.',
      keywords: ['troubleshoot', 'error', 'missing price', 'failed import', 'wrong total', 'empty chart', 'login'],
      content: (
        <>
          <div className="help-troubleshooting">
            <div><strong>A stock has no live price</strong><p>The security may be delisted or suspended, the stored symbol/exchange may not match the data provider, or the provider may be rate-limited. StockHunt may show the last transaction price and exclude the holding from totals.</p></div>
            <div><strong>An import fails</strong><p>Confirm the import type, extension, original file format, and PDF password. For generic broker files, verify that symbol/ISIN, date, side, quantity, and price columns are present.</p></div>
            <div><strong>Quantity or cost is wrong</strong><p>Open the holding’s transaction history. Look for duplicates, missing sells/redemptions, incorrect dates, or a holdings-reconciliation adjustment that does not match the broker report.</p></div>
            <div><strong>XIRR is unavailable</strong><p>There may be too little transaction history, invalid dates, no current value, or cash flows that cannot produce a meaningful annualized return.</p></div>
            <div><strong>A chart or metric is empty</strong><p>The external data source may not provide that history or fundamental field. Try another date range and confirm that the instrument identifier is correct.</p></div>
            <div><strong>You are unexpectedly signed out</strong><p>The saved session token may have expired or become invalid. Sign in again. If the issue repeats, clear the site’s stored data and retry.</p></div>
          </div>
        </>
      )
    },
    {
      id: 'privacy-safety',
      icon: 'lock',
      title: 'Privacy, security, and data accuracy',
      summary: 'Use financial documents safely and understand the limits of market and planning data.',
      keywords: ['privacy', 'security', 'password', 'pan', 'pran', 'accuracy', 'disclaimer'],
      content: (
        <>
          <ul>
            <li>Use a unique account password and log out on shared devices.</li>
            <li>Upload statements only through the intended import form.</li>
            <li>PDF passwords are used to read protected statements; avoid placing PAN, PRAN, or full account numbers in free-text notes.</li>
            <li>Review imported records against official broker, registrar, bank, EPFO, or pension statements.</li>
            <li>Market prices, NAVs, company data, and news come from external services and may be delayed, incomplete, or temporarily unavailable.</li>
            <li>StockHunt is a tracking and planning utility, not investment, tax, legal, or accounting advice.</li>
          </ul>
        </>
      )
    }
  ], []);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = normalizedQuery
    ? sections.filter((section) => (
      section.title.toLowerCase().includes(normalizedQuery)
      || section.summary.toLowerCase().includes(normalizedQuery)
      || section.keywords.some((keyword) => keyword.includes(normalizedQuery))
    ))
    : sections;

  return (
    <>
      {isAuthenticated ? <Navbar /> : <PublicNavbar />}
      <main className="help-page">
        <section className="help-hero">
          <div>
            <Badge bg="light" text="dark" className="help-hero__badge">StockHunt Knowledge Base</Badge>
            <h1>How can we help?</h1>
            <p>Everything you need to set up, maintain, understand, and troubleshoot your financial workspace.</p>
            <Form.Group className="help-search">
              <span aria-hidden="true"><AppIcon name="search" /></span>
              <Form.Control
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search topics, for example: imports, XIRR, NPS..."
                aria-label="Search help topics"
              />
              {query && <Button variant="link" onClick={() => setQuery('')}>Clear</Button>}
            </Form.Group>
          </div>
          <div className="help-hero__start">
            <span>New to StockHunt?</span>
            <strong>Build your financial picture in three steps.</strong>
            <ol>
              <li>Add portfolio transactions</li>
              <li>Record other assets</li>
              <li>Review Dashboard and Analysis</li>
            </ol>
            {isAuthenticated
              ? <Button as={Link as any} to="/portfolio" variant="dark">Open Portfolio</Button>
              : <Button as={Link as any} to="/register" variant="dark">Create an account</Button>}
          </div>
        </section>

        <section className="help-layout">
          <aside className="help-sidebar">
            <div className="help-sidebar__inner">
              <span className="help-sidebar__label">In this guide</span>
              <nav aria-label="Help topics">
                {visibleSections.map((section) => (
                  <a key={section.id} href={`#${section.id}`}>
                    <span className="help-sidebar__icon"><AppIcon name={section.icon} /></span>{section.title}
                  </a>
                ))}
              </nav>
              <div className="help-sidebar__tip">
                <strong>Data looks wrong?</strong>
                <span>Check transaction history before deleting or re-importing anything.</span>
              </div>
            </div>
          </aside>

          <div className="help-content">
            {visibleSections.length > 0 ? visibleSections.map((section) => (
              <Card as="article" className="help-section" id={section.id} key={section.id}>
                <Card.Body>
                  <header className="help-section__header">
                    <span className="help-section__icon" aria-hidden="true"><AppIcon name={section.icon} /></span>
                    <div>
                      <h2>{section.title}</h2>
                      <p>{section.summary}</p>
                    </div>
                  </header>
                  <div className="help-section__body">{section.content}</div>
                </Card.Body>
              </Card>
            )) : (
              <Card className="help-empty">
                <Card.Body>
                  <span className="help-empty__icon"><AppIcon name="search" /></span>
                  <h2>No matching help topics</h2>
                  <p>Try a broader term such as “portfolio”, “import”, “assets”, or “XIRR”.</p>
                  <Button variant="outline-primary" onClick={() => setQuery('')}>Show all topics</Button>
                </Card.Body>
              </Card>
            )}
          </div>
        </section>

        <section className="help-cta">
          <div>
            <span>Suggested next step</span>
            <h2>Review your data from the top down.</h2>
            <p>Start with Dashboard totals, inspect Portfolio transactions, then confirm manually maintained Assets.</p>
          </div>
          <div>
            {isAuthenticated ? (
              <>
                <Button as={Link as any} to="/dashboard" variant="dark">Go to Dashboard</Button>
                <Button as={Link as any} to="/assets" variant="outline-dark">Review Assets</Button>
              </>
            ) : (
              <>
                <Button as={Link as any} to="/login" variant="dark">Sign in</Button>
                <Button as={Link as any} to="/" variant="outline-dark">Back to Home</Button>
              </>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default Help;
